import {
  NavButtonProps,
  NavButtonWithSize,
} from 'Event/Dashboard/components/NavButton'
import React, {useState, useCallback} from 'react'
import {ComponentConfigProps} from 'organization/Event/DashboardConfig/ComponentConfigPanel'
import {NiftyFifty, useNiftyFiftyUpdate} from 'Event/template/NiftyFifty'
import {useForm} from 'react-hook-form'
import {REMOVE} from 'Event/TemplateUpdateProvider'
import MainNavButtonConfigBase from 'Event/Dashboard/components/NavButton/NavButtonConfig/BaseConfig'
import Settings from 'Event/template/NiftyFifty/Dashboard/MainNav/MainNavButton/MainNavButtonConfig/Settings'
import Styling from 'Event/template/NiftyFifty/Dashboard/MainNav/MainNavButton/MainNavButtonConfig/Styling'
import {usePruneAssets} from 'lib/asset'
import {
  useTemplateEditor,
  useAutoUpdate,
} from 'organization/Event/TemplateEditor'
import {useAddTranslation, useRemoveTranslations} from 'Event/LanguageProvider'
import {generateHashId} from 'lib/crypto/hash'

export type ButtonConfigProps<K extends NavButtonProps> = {
  button: K
  update: <T extends keyof K>(key: T) => (value: K[T]) => void
}

export default function MainNavButtonConfig(
  props: ComponentConfigProps & {
    button: NavButtonWithSize
    id?: string
  },
) {
  const {button, id, showing: isVisible, onClose} = props
  const updateTemplate = useNiftyFiftyUpdate()
  const pruneAssets = usePruneAssets()
  const {saved} = useTemplateEditor<NiftyFifty>()
  const removeTranslations = useRemoveTranslations()
  const addTranslation = useAddTranslation()

  const [rules, setRules] = useState(button.rules)
  const {
    control,
    register,
    handleSubmit,
    watch,
    setValue,
    formState,
  } = useForm()

  useAutoUpdate({
    values: {
      mainNav: {
        buttons: {
          [id!]: watch(),
        },
      },
    },
    when: Boolean(id) && isVisible,
  })

  const update = (id: string, updated: NavButtonWithSize) => {
    const image = updated.image
    if (saved.mainNav.buttons && id in saved.mainNav.buttons) {
      pruneAssets({image}, saved.mainNav.buttons[id])
    }

    updateTemplate({
      localization: addTranslation({
        [`${id}_text`]: updated.text,
      }),
      mainNav: {
        buttons: {
          [id]: {
            ...updated,
            text: `{{${id}_text}}`,
          },
        },
      },
    })
  }

  const removeButton = useCallback(() => {
    if (!id) {
      throw new Error('Missing button id')
    }

    onClose()
    updateTemplate({
      localization: removeTranslations([`${id}_text`]),
      mainNav: {
        buttons: {
          [id]: REMOVE,
        },
      },
    })
  }, [id, onClose, updateTemplate, removeTranslations])

  const save = (formData: NavButtonWithSize) => {
    const data: NavButtonWithSize = {
      ...formData,
      rules,
      // isImageUpload: isImageUploadButton,
    }

    if (id) {
      update(id, data)
    } else {
      const numButtons = Object.keys(saved.mainNav.buttons).length
      const position = numButtons + 1
      generateHashId([
        'niftyfifty_',
        'main_nav_button',
        position.toString(),
        new Date().valueOf().toString(),
        Math.random().toString(),
      ]).then((id) => {
        const componentId = `button_${id}`
        update(componentId, {...data, position})
      })
    }
    onClose()
  }

  return (
    <MainNavButtonConfigBase
      button={button}
      id={id}
      control={control}
      register={register}
      watch={watch}
      setValue={setValue}
      rules={rules}
      setRules={setRules}
      removeButton={removeButton}
      onClose={onClose}
      handleSubmit={handleSubmit}
      save={save}
      formState={formState}
      isVisible={isVisible}
      showing={Boolean(id)}
      customSettings={
        <Settings
          button={button}
          control={control}
          watch={watch}
          register={register}
          setValue={setValue}
        />
      }
      customStyling={
        <Styling button={button} control={control} watch={watch} />
      }
    />
  )
}
